import { EntityType } from '../../types.generated';
import { GenericEntityProperties } from '../entity/shared/types';
import {
    GlossaryActionStateOptions,
    GlossaryActionStates,
    GlossarySelectAllOptions,
    GlossarySelectAllHandlers,
} from './types';

// Re-export types for backward compatibility
export type { RelationRowData, SectionType, SectionConfig } from './types';

export const ROOT_NODES = 'rootNodes';
export const ROOT_TERMS = 'rootTerms';

export function getGlossaryRootToUpdate(entityType: EntityType) {
    return entityType === EntityType.GlossaryTerm ? ROOT_TERMS : ROOT_NODES;
}

// Get the urns or special constants for root nodes or terms (above) that need to be refreshed in the Glossary
// sidebar when making updates (edit name, create term/term group, delete term/term group, move entity)
export function getParentNodeToUpdate(entityData: GenericEntityProperties | null, entityType: EntityType) {
    return entityData?.parentNodes?.nodes.length
        ? entityData?.parentNodes?.nodes[0].urn
        : getGlossaryRootToUpdate(entityType);
}

// Add the parent nodes that need to refetch from the glossary sidebar to `urnsToUpdate` state.
// This could also include ROOT_NODES or ROOT_TERMS if the item(s) that need updating don't have parents.
export function updateGlossarySidebar(
    parentNodesToUpdate: string[],
    urnsToUpdate: string[],
    setUrnsToUpdate: (updatdUrns: string[]) => void,
) {
    setUrnsToUpdate([...urnsToUpdate, ...parentNodesToUpdate]);
}

/**
 * Extracts a user-friendly error message from an unknown error type
 */
export const getErrorMessage = (error: unknown): string => {
    return error instanceof Error ? error.message : String(error);
};

/**
 * Computes which selected items are deletable / exportable based on per-entity-type privileges.
 * Shared between BusinessGlossaryPage (toolbar buttons) and GlossaryActions.
 */
export function resolveGlossaryBulkActionStates({
    terms,
    nodes,
    selectedItems,
    canDeleteTerm,
    canDeleteNode,
    canExport,
}: GlossaryActionStateOptions): GlossaryActionStates {
    const hasSelectedTerms = selectedItems.some((urn) => terms.some((term) => term.urn === urn));
    const hasSelectedNodes = selectedItems.some((urn) => nodes.some((node) => node.urn === urn));
    return {
        hasSelectedDeletableItems: (canDeleteTerm && hasSelectedTerms) || (canDeleteNode && hasSelectedNodes),
        hasSelectedExportableItems: canExport && (hasSelectedTerms || hasSelectedNodes),
    };
}

/**
 * Builds the input payload for the exportEntity mutation.
 * Shared between BusinessGlossaryPage and GlossaryActions.
 */
export function buildGlossaryExportInput(
    nodes: Array<{ urn?: string | null }>,
    terms: Array<{ urn?: string | null }>,
    selectedItems: string[],
) {
    return {
        glossaryTerm: {
            glossaryNodeUrns: nodes
                .filter((item) => selectedItems.includes(item?.urn || ''))
                .map((item) => item?.urn || ''),
            glossaryTermUrns: terms
                .filter((item) => selectedItems.includes(item?.urn || ''))
                .map((item) => item?.urn || ''),
        },
        entityType: 'glossaryTerm',
    };
}

/**
 * Builds privilege-aware bulk-select handlers for glossary lists.
 *
 * Key behaviours:
 *  - Only URNs the user can actually interact with (delete or export) are ever
 *    added to the selection — rows the user has no privilege over are skipped.
 *  - Nodes are ordered before terms, matching the combined list rendered by
 *    BusinessGlossaryPage and ChildrenTab.
 *  - All state changes are scoped to the current page; items on other pages
 *    are left untouched when toggling.
 *
 * Returns three handlers consumed by the "Select All" checkbox:
 *  - handleSelectAllSelectable — toggles selectable items on the current page
 *  - isAllSelectableSelected   — true when every selectable item is checked
 *  - isSelectableIndeterminate — true when only some selectable items are checked
 */
export function buildGlossaryBulkSelectHandlers({
    page,
    nodes,
    terms,
    pageSize,
    selectedItems,
    canInteractWithTerm,
    canInteractWithNode,
    setSelectedItems,
}: GlossarySelectAllOptions): GlossarySelectAllHandlers {
    // Nodes come first — mirrors the combined list rendering order.
    const currentPageUrns = [...nodes, ...terms]
        .slice((page - 1) * pageSize, page * pageSize)
        .map((item) => item.urn || '');

    // Filter to URNs the user has permission to interact with.
    // Use a Set for O(1) term/node classification instead of repeated .find() calls.
    const termUrnSet = new Set(terms.map((term) => term.urn || ''));
    const selectableUrns = currentPageUrns.filter((urn) =>
        termUrnSet.has(urn) ? canInteractWithTerm : canInteractWithNode,
    );

    // Compute once — shared by both checkbox state helpers below.
    const selectedCount = selectableUrns.filter((urn) => selectedItems.includes(urn)).length;

    const handleSelectAllSelectable = (checked: boolean) => {
        if (checked) {
            setSelectedItems(Array.from(new Set([...selectedItems, ...selectableUrns])));
        } else {
            setSelectedItems(selectedItems.filter((urn) => !currentPageUrns.includes(urn)));
        }
    };

    const isAllSelectableSelected = () => selectableUrns.length > 0 && selectedCount === selectableUrns.length;
    const isSelectableIndeterminate = () => selectedCount > 0 && selectedCount < selectableUrns.length;

    return { handleSelectAllSelectable, isAllSelectableSelected, isSelectableIndeterminate };
}
